/**
 * Finding Response Modal
 * Allows facility managers to respond to audit findings
 */

import {
  Modal,
  Select,
  Textarea,
  TextInput,
  Button,
  Stack,
  Group,
  Alert,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useAppForm } from "../../hooks/useAppForm";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import { rulesToMantineValidate } from "../../utils/validation";
import {
  FINDING_RESPONSE_FIELD_RULES,
  FINDING_RESPONSE_FIELD_LABELS,
} from "@livingsyncs/types";
import {
  CreateFindingResponseInput,
  ResponseType,
  AuditFinding,
} from "../../types/audit";
import { useCreateFindingResponse } from "../../hooks/useAuditAnnotations";
import { useState } from "react";

interface FindingResponseModalProps {
  opened: boolean;
  onClose: () => void;
  finding: AuditFinding;
  onSuccess?: () => void;
}

const responseTypeOptions: Array<{
  value: ResponseType;
  label: string;
  description: string;
}> = [
  {
    value: "acknowledgment",
    label: "Acknowledgment",
    description: "Acknowledge receipt of the finding",
  },
  {
    value: "action_plan",
    label: "Action Plan",
    description: "Provide a plan to address the finding",
  },
  {
    value: "progress_update",
    label: "Progress Update",
    description: "Update on ongoing corrective actions",
  },
  {
    value: "completion_evidence",
    label: "Completion Evidence",
    description: "Provide evidence that finding has been resolved",
  },
  {
    value: "comment",
    label: "Comment",
    description: "General comment or clarification",
  },
];

export function FindingResponseModal({
  opened,
  onClose,
  finding,
  onSuccess,
}: FindingResponseModalProps) {
  const createResponse = useCreateFindingResponse(finding.id);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // US-082: shared rule map carries required + max-length bounds; the
  // response-type conditionals below wrap the generated validators so the
  // max-length bound still applies once the conditional requirement passes.
  // Backend-only rules come back as 400 details onSubmitSafe maps inline.
  const ruleValidators = rulesToMantineValidate(
    FINDING_RESPONSE_FIELD_RULES,
    FINDING_RESPONSE_FIELD_LABELS,
  );

  const form = useAppForm<CreateFindingResponseInput>(
    {
      initialValues: {
        response_type: "acknowledgment",
        response_text: "",
        action_plan: "",
        responsible_person: "",
        target_completion_date: undefined,
        actual_completion_date: undefined,
        evidence_description: "",
      },
      validate: {
        ...ruleValidators,
        action_plan: (value, values) => {
          if (
            values.response_type === "action_plan" &&
            (!value || value.trim().length === 0)
          ) {
            return "Action plan is required for this response type";
          }
          return ruleValidators.action_plan(value);
        },
        evidence_description: (value, values) => {
          if (
            values.response_type === "completion_evidence" &&
            (!value || value.trim().length === 0)
          ) {
            return "Evidence description is required for completion evidence";
          }
          return ruleValidators.evidence_description(value);
        },
      },
    },
    {
      errorFallback: "Failed to submit response",
      dateOnlyFields: ["target_completion_date", "actual_completion_date"],
    },
  );

  const handleSubmit = async (values: CreateFindingResponseInput) => {
    setIsSubmitting(true);

    try {
      await createResponse.mutateAsync(values);

      notifications.show({
        title: "Response Submitted",
        message: "Your response has been successfully submitted to the auditor",
        color: "green",
      });

      form.reset();
      onSuccess?.();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const selectedResponseType = form.values.response_type;
  const showActionPlanFields =
    selectedResponseType === "action_plan" ||
    selectedResponseType === "progress_update";
  const showCompletionFields = selectedResponseType === "completion_evidence";

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Respond to Finding"
      size="lg"
    >
      <form onSubmit={form.onSubmitSafe(handleSubmit)}>
        <Stack gap="md">
          {/* Finding Summary */}
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            <strong>{finding.title}</strong>
            <br />
            <small>
              {finding.finding_type} • {finding.severity}
            </small>
          </Alert>

          {/* Response Type */}
          <Select
            label="Response Type"
            withAsterisk
            data={responseTypeOptions.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            {...form.getInputProps("response_type")}
            onChange={(value) => {
              form.setFieldValue("response_type", value as ResponseType);
              // Reset conditional fields when type changes
              if (value !== "action_plan" && value !== "progress_update") {
                form.setFieldValue("action_plan", "");
                form.setFieldValue("responsible_person", "");
                form.setFieldValue("target_completion_date", undefined);
              }
              if (value !== "completion_evidence") {
                form.setFieldValue("evidence_description", "");
                form.setFieldValue("actual_completion_date", undefined);
              }
            }}
          />

          {/* Response Text */}
          <Textarea
            label="Response"
            withAsterisk
            placeholder="Provide your response to this finding..."
            minRows={4}
            {...form.getInputProps("response_text")}
          />

          {/* Action Plan Fields */}
          {showActionPlanFields && (
            <>
              <Textarea
                label="Action Plan"
                withAsterisk={selectedResponseType === "action_plan"}
                placeholder="Describe the corrective actions to be taken..."
                minRows={3}
                {...form.getInputProps("action_plan")}
              />

              <TextInput
                label="Responsible Person"
                placeholder="Name of person responsible for implementation"
                {...form.getInputProps("responsible_person")}
              />

              <DatePickerInput
                label="Target Completion Date"
                placeholder="Select target completion date"
                minDate={new Date()}
                valueFormat="YYYY-MM-DD"
                value={form.values.target_completion_date || null}
                onChange={(date) => {
                  form.setFieldValue(
                    "target_completion_date",
                    date || undefined,
                  );
                }}
                clearable
              />
            </>
          )}

          {/* Completion Evidence Fields */}
          {showCompletionFields && (
            <>
              <Textarea
                label="Evidence Description"
                withAsterisk
                placeholder="Describe the evidence that demonstrates resolution..."
                minRows={3}
                {...form.getInputProps("evidence_description")}
              />

              <DatePickerInput
                label="Actual Completion Date"
                placeholder="Select actual completion date"
                maxDate={new Date()}
                valueFormat="YYYY-MM-DD"
                value={form.values.actual_completion_date || null}
                onChange={(date) => {
                  form.setFieldValue(
                    "actual_completion_date",
                    date || undefined,
                  );
                }}
                clearable
              />
            </>
          )}

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Submit Response
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
